import React, { useState, useEffect, useCallback } from 'react';
import { roleAPI, permissionAPI } from '../../services/api';
import { Button, Input, Alert, Modal, Loading } from '../../components/common';
import { useAuth } from '../../contexts/AuthContext';
import * as permissionUtils from '../../utils/permissions';
import './AdminPages.css';

const RolesPage = () => {
  const { user: currentUser } = useAuth();
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });

  const [showModal, setShowModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [formLoading, setFormLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    permission_ids: [],
    is_active: true,
  });

  const fetchData = useCallback(async () => {
    try {
      const [rolesRes, permsRes] = await Promise.all([
        roleAPI.list(),
        permissionAPI.list(),
      ]);
      setRoles(rolesRes.data.results || rolesRes.data);
      setPermissions(permsRes.data.results || permsRes.data);
    } catch (err) {
      setMessage({ type: 'error', text: '获取数据失败' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      description: '',
      permission_ids: [],
      is_active: true,
    });
    setEditingRole(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);

    try {
      if (editingRole) {
        await roleAPI.update(editingRole.id, formData);
        setMessage({ type: 'success', text: '角色更新成功' });
      } else {
        await roleAPI.create(formData);
        setMessage({ type: 'success', text: '角色创建成功' });
      }
      setShowModal(false);
      resetForm();
      fetchData();
    } catch (err) {
      const errors = err.response?.data;
      const errorMsg = errors?.code?.[0] || errors?.name?.[0] || errors?.detail || '操作失败';
      setMessage({ type: 'error', text: errorMsg });
    } finally {
      setFormLoading(false);
    }
  };

  const openEditModal = (role) => {
    setEditingRole(role);
    setFormData({
      name: role.name,
      code: role.code,
      description: role.description || '',
      permission_ids: role.permissions?.map(p => p.id) || [],
      is_active: role.is_active,
    });
    setShowModal(true);
  };

  const handleDelete = async (role) => {
    if (role.is_system) {
      setMessage({ type: 'error', text: '系统角色不可删除' });
      return;
    }

    if (!window.confirm(`确定要删除角色 "${role.name}" 吗？`)) {
      return;
    }

    try {
      await roleAPI.delete(role.id);
      setMessage({ type: 'success', text: '角色已删除' });
      fetchData();
    } catch (err) {
      setMessage({ type: 'error', text: '删除失败' });
    }
  };

  // 按模块分组权限
  const groupedPermissions = permissions.reduce((acc, perm) => {
    const module = perm.module || '其他';
    if (!acc[module]) {
      acc[module] = [];
    }
    acc[module].push(perm);
    return acc;
  }, {});

  if (loading) {
    return <Loading fullScreen text="加载中..." />;
  }

  return (
    <div className="admin-page">
      <div className="page-header">
        <div>
          <h1>角色管理</h1>
          <p>管理系统角色和权限分配</p>
        </div>
        {permissionUtils.canCreateRole(currentUser) && (
          <Button onClick={() => { resetForm(); setShowModal(true); }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 18, height: 18 }}>
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            添加角色
          </Button>
        )}
      </div>

      {message.text && (
        <Alert
          type={message.type}
          message={message.text}
          onClose={() => setMessage({ type: '', text: '' })}
        />
      )}

      <div className="roles-grid">
        {roles.length > 0 ? (
          roles.map((role) => (
            <div key={role.id} className="role-card">
              <div className="role-card-header">
                <div className="role-card-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  </svg>
                </div>
                <div className="role-card-info">
                  <h3>{role.name}</h3>
                  <span className="role-code">{role.code}</span>
                </div>
                <div className="role-card-actions">
                  {!role.is_system && permissionUtils.canCreateRole(currentUser) && (
                    <>
                      <button className="action-btn" onClick={() => openEditModal(role)} title="编辑">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                      </button>
                      <button className="action-btn danger" onClick={() => handleDelete(role)} title="删除">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        </svg>
                      </button>
                    </>
                  )}
                </div>
              </div>
              <p className="role-description">{role.description || '暂无描述'}</p>
              <div className="role-permissions">
                <span className="permissions-label">权限数量：</span>
                <span className="permissions-count">{role.permissions?.length || 0}</span>
              </div>
              <div className="role-card-footer">
                <span className={`status-badge ${role.is_active ? 'active' : 'inactive'}`}>
                  {role.is_active ? '启用' : '禁用'}
                </span>
                {role.is_system && (
                  <span className="system-badge">系统角色</span>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="empty-state">
            <p>暂无角色数据</p>
          </div>
        )}
      </div>

      <Modal
        isOpen={showModal}
        onClose={() => { setShowModal(false); resetForm(); }}
        title={editingRole ? '编辑角色' : '添加角色'}
        size="large"
      >
        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-grid">
            <Input
              label="角色名称 *"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
            <Input
              label="角色编码 *"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value })}
              required
              hint="唯一标识，如：admin"
            />
          </div>

          <div className="form-group">
            <label className="form-label">描述</label>
            <textarea
              className="form-textarea"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={2}
            />
          </div>

          <div className="form-group">
            <label className="form-label">权限分配</label>
            <div className="permissions-select">
              {Object.entries(groupedPermissions).map(([module, perms]) => (
                <div key={module} className="permission-group">
                  <h4 className="permission-group-title">{module}</h4>
                  <div className="checkbox-group">
                    {perms.map((perm) => (
                      <label key={perm.id} className="checkbox-item">
                        <input
                          type="checkbox"
                          disabled={!permissionUtils.canCreateRole(currentUser)}
                          checked={formData.permission_ids.includes(perm.id)}
                          onChange={(e) => {
                            if (!permissionUtils.canCreateRole(currentUser)) return;
                            const newPerms = e.target.checked
                              ? [...formData.permission_ids, perm.id]
                              : formData.permission_ids.filter(id => id !== perm.id);
                            setFormData({ ...formData, permission_ids: newPerms });
                          }}
                        />
                        <span>{perm.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
              {Object.keys(groupedPermissions).length === 0 && (
                <p className="empty-text">暂无可分配的权限</p>
              )}
            </div>
            {!permissionUtils.canCreateRole(currentUser) && (
              <p style={{ fontSize: '0.875rem', color: '#d97706', marginTop: '0.5rem' }}>
                仅超级管理员可以管理角色和权限
              </p>
            )}
          </div>

          <div className="form-group">
            <label className="checkbox-item">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              />
              <span>启用角色</span>
            </label>
          </div>

          <div className="modal-actions">
            <Button type="button" variant="secondary" onClick={() => setShowModal(false)}>
              取消
            </Button>
            <Button type="submit" loading={formLoading}>
              {editingRole ? '保存' : '创建'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default RolesPage;

